'use client'

import { useRouter } from '@/i18n/navigation'
import { routing } from '@/i18n/routing'
import { SessionStorage } from '@/lib/utils/session-storage'
import { showPopup } from '@/providers/popup-provider'
import { useEffect } from 'react'
import { LoginForm } from './login-form'
import { RegisterForm } from './register-form'

type OpenAuthOptions = {
  // Action cần làm tiếp sau khi đăng nhập — có callback thì luôn mở popup (kể cả
  // mobile) vì điều hướng sang trang sẽ mất callback.
  callback?: () => void
}

// Breakpoint khớp với ResponsiveModal (lg) — dưới lg popup hiển thị dạng bottom sheet
const MOBILE_MEDIA_QUERY = '(max-width: 1023px)'

const navigateRef: { current: ((href: string) => void) | null } = {
  current: null,
}

/**
 * Đăng ký router cho các hàm mở auth (gọi được ngoài React component).
 * Mount một lần trong AppProviders.
 */
export function AuthNavigationBridge() {
  const router = useRouter()

  useEffect(() => {
    navigateRef.current = (href) => router.push(href)
    return () => {
      navigateRef.current = null
    }
  }, [router])

  return null
}

const isMobileViewport = () =>
  typeof window !== 'undefined' && window.matchMedia(MOBILE_MEDIA_QUERY).matches

// URL hiện tại không kèm locale prefix (router của next-intl tự thêm lại)
const getCurrentUrlWithoutLocale = () => {
  const localePrefix = new RegExp(`^/(${routing.locales.join('|')})(?=/|$)`)
  const pathname = window.location.pathname.replace(localePrefix, '') || '/'
  return pathname + window.location.search
}

const openAuthPage = (path: '/login' | '/register') => {
  const redirectUrl = getCurrentUrlWithoutLocale()
  // Lưu vào sessionStorage để giữ redirect khi chuyển qua lại login/register/OTP
  SessionStorage.setWithExpiry('auth_redirect_url', redirectUrl, 10)

  const href = `${path}?redirect=${encodeURIComponent(redirectUrl)}`
  if (navigateRef.current) {
    navigateRef.current(href)
  } else {
    window.location.href = href
  }
}

export const openLogin = ({ callback }: OpenAuthOptions = {}) => {
  if (!callback && isMobileViewport()) {
    openAuthPage('/login')
    return
  }
  showPopup(<LoginForm callback={callback} />)
}

export const openRegister = ({ callback }: OpenAuthOptions = {}) => {
  if (!callback && isMobileViewport()) {
    openAuthPage('/register')
    return
  }
  showPopup(<RegisterForm callback={callback} />)
}
